/**
 * Integration Status Component
 * Monitors the health and performance of Team A + Team B component integration
 * Provides real-time diagnostics and error handling for cross-component communication
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Wifi,
  WifiOff,
  Clock,
  Zap,
  Database,
  Layers,
  Monitor,
  Settings,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Gauge,
  Bug,
  Shield,
  Globe,
} from 'lucide-react';

import { MagicCard } from '../ui/magic/MagicCard';
import { NumberTicker } from '../ui/magic/NumberTicker';
import { AnimatedCircularProgressBar } from '../ui/magic/AnimatedCircularProgressBar';
import { cn } from '../../lib/utils';

// Integration Health Types
interface ComponentHealth {
  teamAComponents: {
    dashboardMetrics: 'healthy' | 'warning' | 'error';
    sustainabilityAnalytics: 'healthy' | 'warning' | 'error';
    stakeholderInterface: 'healthy' | 'warning' | 'error';
    projectManagement: 'healthy' | 'warning' | 'error';
    bentoGridSystem: 'healthy' | 'warning' | 'error';
  };
  teamBComponents: {
    routeVisualization: 'healthy' | 'warning' | 'error';
    realTimeStatus: 'healthy' | 'warning' | 'error';
    vehicleTracking: 'healthy' | 'warning' | 'error';
    optimizationEngine: 'healthy' | 'warning' | 'error';
    animationSystem: 'healthy' | 'warning' | 'error';
  };
}

interface PerformanceMetrics {
  renderTime: number; // ms
  bundleSize: number; // KB
  memoryUsage: number; // MB
  animationFPS: number;
  errorRate: number; // percentage
  uptime: number; // hours
}

interface IntegrationMetrics {
  crossComponentCommunication: 'excellent' | 'good' | 'poor' | 'failed';
  dataSync: 'synced' | 'delayed' | 'out_of_sync' | 'failed';
  performanceScore: number; // 0-100
  accessibilityScore: number; // 0-100
  lastHealthCheck: Date;
  issues: IntegrationIssue[];
}

interface IntegrationIssue {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  component: string;
  message: string;
  timestamp: Date;
  resolved: boolean;
}

interface IntegrationStatusProps {
  className?: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
  showDetailedMetrics?: boolean;
}

export const IntegrationStatus: React.FC<IntegrationStatusProps> = ({
  className,
  autoRefresh = true,
  refreshInterval = 10000,
  showDetailedMetrics = true,
}) => {
  // State management
  const [componentHealth, setComponentHealth] = useState<ComponentHealth>({
    teamAComponents: {
      dashboardMetrics: 'healthy',
      sustainabilityAnalytics: 'healthy',
      stakeholderInterface: 'healthy',
      projectManagement: 'healthy',
      bentoGridSystem: 'healthy',
    },
    teamBComponents: {
      routeVisualization: 'healthy',
      realTimeStatus: 'healthy',
      vehicleTracking: 'healthy',
      optimizationEngine: 'healthy',
      animationSystem: 'healthy',
    },
  });

  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>({
    renderTime: 45,
    bundleSize: 485,
    memoryUsage: 38,
    animationFPS: 60,
    errorRate: 0.02,
    uptime: 24.5,
  });

  const [integrationMetrics, setIntegrationMetrics] = useState<IntegrationMetrics>({
    crossComponentCommunication: 'excellent',
    dataSync: 'synced',
    performanceScore: 95,
    accessibilityScore: 98,
    lastHealthCheck: new Date(),
    issues: [],
  });

  const [isRefreshing, setIsRefreshing] = useState(false);

  // Calculate overall health score
  const overallHealth = useMemo(() => {
    const teamAHealthy = Object.values(componentHealth.teamAComponents).filter(h => h === 'healthy').length;
    const teamBHealthy = Object.values(componentHealth.teamBComponents).filter(h => h === 'healthy').length;
    const totalComponents = Object.keys(componentHealth.teamAComponents).length + Object.keys(componentHealth.teamBComponents).length;
    
    return Math.round((teamAHealthy + teamBHealthy) / totalComponents * 100);
  }, [componentHealth]);

  // Simulate performance monitoring
  const performHealthCheck = async () => {
    setIsRefreshing(true);
    
    // Simulate health check delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Generate realistic performance variations
    setPerformanceMetrics(prev => ({
      ...prev,
      renderTime: 35 + Math.random() * 20, // 35-55ms
      memoryUsage: 35 + Math.random() * 10, // 35-45MB
      animationFPS: 58 + Math.random() * 4, // 58-62fps
      errorRate: Math.random() * 0.1, // 0-0.1%
      uptime: prev.uptime + 0.1,
    }));

    // Occasionally introduce issues for testing
    if (Math.random() < 0.1) {
      const newIssue: IntegrationIssue = {
        id: Date.now().toString(),
        severity: Math.random() < 0.5 ? 'low' : 'medium',
        component: Math.random() < 0.5 ? 'Team A Dashboard' : 'Team B Visualization',
        message: 'Temporary sync delay detected',
        timestamp: new Date(),
        resolved: false,
      };
      
      setIntegrationMetrics(prev => ({
        ...prev,
        issues: [...prev.issues.slice(-4), newIssue],
        lastHealthCheck: new Date(),
      }));
    }

    setIntegrationMetrics(prev => ({
      ...prev,
      performanceScore: Math.max(85, Math.min(100, prev.performanceScore + (Math.random() - 0.5) * 5)),
      lastHealthCheck: new Date(),
    }));

    setIsRefreshing(false);
  };

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(performHealthCheck, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const getHealthIcon = (health: 'healthy' | 'warning' | 'error') => {
    switch (health) {
      case 'healthy': return CheckCircle2;
      case 'warning': return AlertTriangle;
      case 'error': return AlertCircle;
      default: return CheckCircle2;
    }
  };

  const getHealthColor = (health: 'healthy' | 'warning' | 'error') => {
    switch (health) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  const getSyncIcon = (status: IntegrationMetrics['dataSync']) => {
    switch (status) {
      case 'synced': return Wifi;
      case 'delayed': return Clock;
      case 'out_of_sync': return AlertTriangle;
      case 'failed': return WifiOff;
      default: return Wifi;
    }
  };

  const getSyncColor = (status: IntegrationMetrics['dataSync']) => {
    switch (status) {
      case 'synced': return 'text-green-600 bg-green-100';
      case 'delayed': return 'text-yellow-600 bg-yellow-100';
      case 'out_of_sync': return 'text-orange-600 bg-orange-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-green-100 to-blue-100">
            <Activity className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Integration Status</h2>
            <p className="text-gray-600">
              Team A + Team B system health monitoring
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Overall Health Indicator */}
          <motion.div
            className={cn(
              'flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium',
              overallHealth >= 90 ? 'bg-green-100 text-green-700' :
              overallHealth >= 70 ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            )}
            animate={{ scale: overallHealth < 70 ? [1, 1.05, 1] : 1 }}
            transition={{ duration: 1, repeat: overallHealth < 70 ? Infinity : 0 }}
          >
            <Shield className="w-4 h-4" />
            <span>{overallHealth}% Healthy</span>
          </motion.div>

          {/* Refresh Button */}
          <button
            onClick={performHealthCheck}
            disabled={isRefreshing}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Overall Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MagicCard className="p-4 text-center">
          <Gauge className="w-6 h-6 text-blue-600 mx-auto mb-2" />
          <NumberTicker
            value={integrationMetrics.performanceScore}
            suffix="/100"
            className="text-xl font-bold text-blue-600"
            duration={2.0}
          />
          <p className="text-xs text-gray-600">Performance</p>
        </MagicCard>

        <MagicCard className="p-4 text-center">
          <Shield className="w-6 h-6 text-green-600 mx-auto mb-2" />
          <NumberTicker
            value={integrationMetrics.accessibilityScore}
            suffix="/100"
            className="text-xl font-bold text-green-600"
            duration={2.2}
          />
          <p className="text-xs text-gray-600">Accessibility</p>
        </MagicCard>

        <MagicCard className="p-4 text-center">
          <Monitor className="w-6 h-6 text-purple-600 mx-auto mb-2" />
          <NumberTicker
            value={performanceMetrics.animationFPS}
            suffix=" FPS"
            className="text-xl font-bold text-purple-600"
            duration={2.4}
          />
          <p className="text-xs text-gray-600">Animation</p>
        </MagicCard>

        <MagicCard className="p-4 text-center">
          <Clock className="w-6 h-6 text-orange-600 mx-auto mb-2" />
          <NumberTicker
            value={performanceMetrics.uptime}
            suffix="h"
            className="text-xl font-bold text-orange-600"
            duration={2.6}
          />
          <p className="text-xs text-gray-600">Uptime</p>
        </MagicCard>
      </div>

      {/* Component Health Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Team A Components */}
        <MagicCard className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <h3 className="text-lg font-semibold text-gray-900">Team A Components</h3>
          </div>
          
          <div className="space-y-3">
            {Object.entries(componentHealth.teamAComponents).map(([component, health]) => {
              const HealthIcon = getHealthIcon(health);
              return (
                <div key={component} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={cn('p-1 rounded-lg', getHealthColor(health))}>
                      <HealthIcon className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-gray-900 capitalize">
                      {component.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                  <span className={cn(
                    'text-xs px-2 py-1 rounded-full font-medium capitalize',
                    getHealthColor(health)
                  )}>
                    {health}
                  </span>
                </div>
              );
            })}
          </div>
        </MagicCard>

        {/* Team B Components */}
        <MagicCard className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-3 h-3 bg-purple-500 rounded-full" />
            <h3 className="text-lg font-semibold text-gray-900">Team B Components</h3>
          </div>
          
          <div className="space-y-3">
            {Object.entries(componentHealth.teamBComponents).map(([component, health]) => {
              const HealthIcon = getHealthIcon(health);
              return (
                <div key={component} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={cn('p-1 rounded-lg', getHealthColor(health))}>
                      <HealthIcon className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-gray-900 capitalize">
                      {component.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                  <span className={cn(
                    'text-xs px-2 py-1 rounded-full font-medium capitalize',
                    getHealthColor(health)
                  )}>
                    {health}
                  </span>
                </div>
              );
            })}
          </div>
        </MagicCard>
      </div>

      {/* Performance Metrics */}
      {showDetailedMetrics && (
        <MagicCard className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <Database className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <NumberTicker
                value={performanceMetrics.bundleSize}
                suffix=" KB"
                className="text-lg font-bold text-blue-600"
                duration={1.5}
              />
              <p className="text-sm text-gray-600">Bundle Size</p>
            </div>

            <div className="text-center">
              <Zap className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <NumberTicker
                value={performanceMetrics.renderTime}
                suffix=" ms"
                className="text-lg font-bold text-green-600"
                duration={1.7}
              />
              <p className="text-sm text-gray-600">Render Time</p>
            </div>

            <div className="text-center">
              <Layers className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <NumberTicker
                value={performanceMetrics.memoryUsage}
                suffix=" MB"
                className="text-lg font-bold text-purple-600"
                duration={1.9}
              />
              <p className="text-sm text-gray-600">Memory Usage</p>
            </div>

            <div className="text-center">
              <Bug className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <NumberTicker
                value={performanceMetrics.errorRate}
                suffix="%"
                className="text-lg font-bold text-red-600"
                duration={2.1}
              />
              <p className="text-sm text-gray-600">Error Rate</p>
            </div>
          </div>
        </MagicCard>
      )}

      {/* Integration Communication */}
      <MagicCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Cross-Component Communication</h3>
          
          <div className="flex items-center space-x-2">
            {React.createElement(getSyncIcon(integrationMetrics.dataSync), {
              className: cn('w-5 h-5', getSyncColor(integrationMetrics.dataSync).split(' ')[0])
            })}
            <span className={cn(
              'text-sm px-2 py-1 rounded-full font-medium capitalize',
              getSyncColor(integrationMetrics.dataSync)
            )}>
              {integrationMetrics.dataSync.replace('_', ' ')}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Globe className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <div className="text-sm font-medium text-blue-900">Dashboard ↔ Visualization</div>
            <div className="text-xs text-blue-600 mt-1">Team A + Team B</div>
          </div>

          <div className="text-center p-4 bg-green-50 rounded-lg">
            <Activity className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <div className="text-sm font-medium text-green-900">Analytics ↔ Real-time</div>
            <div className="text-xs text-green-600 mt-1">Data Synchronization</div>
          </div>

          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <Settings className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <div className="text-sm font-medium text-purple-900">Collaboration ↔ Tracking</div>
            <div className="text-xs text-purple-600 mt-1">State Management</div>
          </div>
        </div>
      </MagicCard>

      {/* Recent Issues */}
      {integrationMetrics.issues.length > 0 && (
        <MagicCard className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Issues</h3>
          
          <div className="space-y-3">
            {integrationMetrics.issues.slice(-3).map(issue => (
              <div key={issue.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className={cn(
                  'p-1 rounded-lg mt-0.5',
                  issue.severity === 'critical' ? 'text-red-600 bg-red-100' :
                  issue.severity === 'high' ? 'text-orange-600 bg-orange-100' :
                  issue.severity === 'medium' ? 'text-yellow-600 bg-yellow-100' :
                  'text-blue-600 bg-blue-100'
                )}>
                  <AlertTriangle className="w-4 h-4" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{issue.component}</span>
                    <span className="text-xs text-gray-500">
                      {issue.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{issue.message}</p>
                </div>
              </div>
            ))}
          </div>
        </MagicCard>
      )}

      {/* Status Footer */}
      <MagicCard className="p-4 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span>Integration Framework: Operational</span>
            </div>
            <div className="flex items-center space-x-1">
              <Activity className="w-4 h-4 text-blue-600" />
              <span>Monitoring: Active</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>Last check: {integrationMetrics.lastHealthCheck.toLocaleTimeString()}</span>
          </div>
        </div>
      </MagicCard>
    </div>
  );
};